import React, { useState, useEffect } from "react";
import NavLogo1 from "./../../assets/Images/RF-logo-dark.svg";
import NavLogo2 from "./../../assets/Images/cold-core-logo.svg";
import CustomerLogo from "./../../assets/Images/Logo-1.2.png";
import UserIcon from "./../../assets/Images/person.svg";
import { NavLink, useNavigate, Link } from "react-router-dom";
import NAVBAR_MENU_PERMISSIONS from "../../utils/navbarMenuPermissions";
import useGetData from "../Custom-Hooks/useGetData";
import { truncateText } from "../../utils/textTruncateUtils";
import userProfileImage from "./../../assets/Images/transparent-user-profile.png";
import { downloadWopiFile } from "../../services/wopi.service";
import axios from "axios";
import { useDashboard } from "../../context/DashboardContext";
import { useAppContext } from "../../context/AppContext.tsx";

const Navbar = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const userDetails = JSON.parse(localStorage.getItem("userDetails")) || "";
  const isAdmin = userDetails?.role_name === "Admin";
  const userId = localStorage.getItem("id") || "";
  const roleName = localStorage.getItem("role_name") || "";
  const selectedLocation = localStorage.getItem("selectedLocation") || "";
  const selectedSystem = localStorage.getItem("selectedSystem") || "";
  const [isFullScreen, setIsFullScreen] = useState(false);
  const navigate = useNavigate();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [userPermissions, setUserPermissions] = useState([]);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [allCustomerData, setAllCustomerData] = useState(null);
  const [customerPreview, setCustomerPreview] = useState(null);
  const { dashboardData, setShouldRefetch } = useDashboard(); // Access the context
  const { user } = useAppContext();

  // get all users data from API
  const {
    data: allUserData,
    isLoading: getLoading,
    refetch,
  } = useGetData(`${baseUrl}/user/${userId}`);

  useEffect(() => {
    if (allUserData?.data?.featurePermissions) {
      setUserPermissions(allUserData.data.featurePermissions);
    }
  }, [allUserData]);

  useEffect(() => {
    if (dashboardData.length === 0) {
      setShouldRefetch(true);
    }
  }, [dashboardData, setShouldRefetch]);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const accessToken = sessionStorage.getItem("accessToken");
        const headers = accessToken ? { Authorization: `${accessToken}` } : {};

        const response = await axios.post(
          `${baseUrl}/customer`,
          { userId: parseInt(userId) },
          { headers }
        );

        setAllCustomerData(response.data);
        const customerId = parseInt(localStorage.getItem("customerId"));

        const matchedCustomer = response.data.find(
          (customer) => customer.id === customerId
        );

        if (matchedCustomer?.logoUrl) {
          const fileResponse = await downloadWopiFile(matchedCustomer.logoUrl, {
            responseType: "blob",
          }, "core");

          const blobUrl = URL.createObjectURL(fileResponse);
          setCustomerPreview(blobUrl);
        }
      } catch (err) {
        console.error("Fetch customer error:", err);
      } finally {
      }
    };

    if (userId) {
      fetchCustomerData();
    }
  }, [userId, navigate]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
    window.dispatchEvent(new Event("themeChange"));
  }, [theme]);

  const handleThemeChange = (value) => {
    setTheme(value);
  };

  useEffect(() => {
    const fetchAvatar = async () => {
      const avatarKey = user?.avatar;
      const avatarURL = allUserData?.data?.avatar;
      if (avatarKey) {
        try {
          const objectUrl = URL.createObjectURL(avatarKey);
          setAvatarPreview(objectUrl);
          return () => URL.revokeObjectURL(objectUrl);
        } catch (err) {
          console.error("Error downloading avatar file:", err);
        }
      } else if (avatarURL) {
        try {
          const blob = await downloadWopiFile(avatarURL);
          const objectUrl = URL.createObjectURL(blob);

          setAvatarPreview(objectUrl);

          return () => URL.revokeObjectURL(objectUrl);
        } catch (err) {
          console.error("Error downloading avatar file:", err);
        }
      }
    };

    fetchAvatar();
  }, [user, allUserData]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem("theme", "light");
    navigate("/login");
  };

  useEffect(() => {
    // Event listener to detect when full-screen mode changes
    const onFullScreenChange = () => {
      if (document.fullscreenElement) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };

    // Adding event listeners for different browsers
    document.addEventListener("fullscreenchange", onFullScreenChange);
    document.addEventListener("webkitfullscreenchange", onFullScreenChange);
    document.addEventListener("mozfullscreenchange", onFullScreenChange);
    document.addEventListener("msfullscreenchange", onFullScreenChange);

    // Cleanup listeners on component unmount
    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullScreenChange
      );
      document.removeEventListener("mozfullscreenchange", onFullScreenChange);
      document.removeEventListener("msfullscreenchange", onFullScreenChange);
    };
  }, []);

  const handleFullScreenToggle = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        // Firefox
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        // IE/Edge
        document.documentElement.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        // Firefox
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        // Chrome, Safari, and Opera
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        // IE/Edge
        document.msExitFullscreen();
      }
    }
  };

  return (
    <div>
      <div className="navbar-header d-flex">
        <div className="nav-logo px-3 py-1">
          <a href="#" className="logo1 border-end">
            <img alt="Logo" src={NavLogo1} />
          </a>
          <a href="#" className="logo2">
            <img alt="Logo" src={NavLogo2} />
          </a>
        </div>

        <div className="navbar-links">
          <ul className="nav-custom-list">
            {Object.entries(NAVBAR_MENU_PERMISSIONS).map(([id, name]) => {
              const isDashboard = name === "Dashboard";
              const path = {
                Dashboard: "/dashboard",
                "Doc Store": "/doc-store",
                "Contact Us": "/contact",
              }[name];

              const hasAccess = isAdmin || userPermissions.includes(Number(id));

              if (!hasAccess) return null;

              return (
                <li className="nav-custom-item" key={id}>
                  {isDashboard ? (
                    <div className="nav-dropdown" style={{ marginTop: "-2px" }}>
                      <span className="nav-custom-link dropdown-toggle">
                        Dashboard
                      </span>
                      <ul className="dropdown-menu">
                        {dashboardData?.map((page) =>
                          page.status ? (
                            <li key={page.pageId}>
                              <NavLink
                                to={`/dashboard/${page.pageId}`}
                                className={({ isActive }) =>
                                  isActive
                                    ? "nav-custom-link active-link"
                                    : "nav-custom-link"
                                }
                              >
                                {page.pageTitle}
                              </NavLink>
                            </li>
                          ) : null
                        )}
                      </ul>
                    </div>
                  ) : (
                    <NavLink
                      to={path}
                      className={({ isActive }) =>
                        isActive
                          ? "nav-custom-link active-link"
                          : "nav-custom-link"
                      }
                    >
                      {name}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="admin-dropdown d-flex align-items-center">
          <div className="px-2 d-flex">
            <div className="customer-logo-container py-1">
              <img
                src={customerPreview || CustomerLogo}
                alt="Customer Logo"
                className="customer-logo-img"
              />
            </div>
            <div className="d-flex flex-column justify-content-center border-end px-3 text-center">
              <div className="fnt10" title={selectedSystem}>
                {truncateText(selectedSystem, 25)}
              </div>
              <div className="fnt10" title={selectedLocation}>
                {truncateText(selectedLocation, 25)}
              </div>
            </div>
          </div>
          {/* Theme Selector Dropdown */}
          <div className="d-flex justify-content-center align-items-center me-3">
            <div className="custom-dropdown">
              <button className="dropdown-btn">
                {theme === "light" ? (
                  <i className="bi bi-sun" style={{ fontSize: "15px" }}></i>
                ) : (
                  <i
                    className="bi bi-moon"
                    style={{ fontSize: "13px", color: "#ffffff" }}
                  ></i>
                )}
              </button>
              <div className="dropdown-content">
                <div
                  className={`dropdown-item ${
                    theme === "light" ? "selected" : ""
                  }`}
                  onClick={() => handleThemeChange("light")}
                >
                  <i
                    className="bi bi-sun"
                    style={{ fontSize: "15px", color: "#25a9e0" }}
                  ></i>
                  <span
                    className={`ps-2 fnt11 fw-600 ${
                      theme === "light" ? "primary-color" : "text-secondary"
                    }`}
                  >
                    Light
                  </span>
                </div>
                <div
                  className={`dropdown-item ${
                    theme === "dark" ? "selected" : ""
                  }`}
                  onClick={() => handleThemeChange("dark")}
                >
                  <i
                    className="bi bi-moon"
                    style={{ fontSize: "13px", color: "#25a9e0" }}
                  ></i>
                  <span
                    className={`ps-2 fnt11 fw-600 ${
                      theme === "dark" ? "primary-color" : "text-secondary"
                    }`}
                  >
                    Dark
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Max-Min View */}
          <div>
            <div
              className="d-flex justify-content-center align-items-center me-4 max-min-icon"
              onClick={handleFullScreenToggle}
              style={{ cursor: "pointer" }}
            >
              <i class="bi bi-fullscreen"></i>
            </div>
          </div>

          {/* Role Dropdown */}
          <div>
            <a
              className="nav-link dropdown-toggle fnt12 fw-500"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {allUserData?.data?.username} &nbsp;
              <img
                src={avatarPreview || userProfileImage}
                alt="Profile-Image"
                className="rounded me-1 rounded-circle"
                width="20"
                height="20"
              />
            </a>
            <ul className="dropdown-menu">
              <li className="px-2 pt-2">
                <Link
                  to="/user-profile"
                  className="dropdown-item rounded-1 fnt12 fw-500"
                >
                  My Profile
                </Link>
              </li>
              <hr />
              {roleName === "Admin" && (
                <>
                  <li className="px-2">
                    <Link
                      to="/user-management"
                      className="dropdown-item rounded-1 fnt12 fw-500"
                    >
                      User Management
                    </Link>
                  </li>
                  <hr />
                </>
              )}
              <li className="px-2">
                <Link
                  to="/user-preference"
                  className="dropdown-item rounded-1 fnt12 fw-500"
                >
                  User Preferences
                </Link>
              </li>
              <hr />
              {roleName === "Admin" && (
                <>
                  <li className="px-2">
                    <Link
                      to="/facility-management"
                      className="dropdown-item rounded-1 fnt12 fw-500"
                    >
                      Enterprise Console
                    </Link>
                  </li>
                  <hr />
                </>
              )}
              <li className="px-2">
                <Link
                  to="/facility"
                  className="dropdown-item rounded-1 fnt12 fw-500"
                >
                  Facility
                </Link>
              </li>
              <hr />
              <li className="px-2 pb-2">
                <button
                  onClick={handleLogout}
                  className="dropdown-item rounded-1 fnt12 fw-500"
                >
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
